import { useEffect } from 'react'
import AOS from 'aos'
import Button from '@ui/Button'
import MetaInfo from '@/components/common/MetaInfo'
import Breadcrumb from '@common/Breadcrumb'
import TransformBanner from '@sections/TransformBanner'
import { ROUTES,SITE } from '@/constants'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.UI_UX_DESIGN)

// Section 1 — UX / UI design process steps
const UX_PROCESS_STEPS = [
  { icon: 'fa-magnifying-glass-chart', title: 'Existing Interface Auditing', desc: 'We review your current product to uncover usability gaps before redesigning anything.' },
  { icon: 'fa-route',                  title: 'User Journey Mapping',        desc: 'We map how real users move through your product to spot friction and drop-off points.' },
  { icon: 'fa-pen-ruler',              title: 'Wireframing & Prototyping',   desc: 'Low-to-high fidelity wireframes and clickable prototypes validate ideas early.' },
  { icon: 'fa-display',                title: 'UX For Web, Apps & SaaS',     desc: 'Consistent, purpose-built experiences across every platform you ship on.' },
]

// Section 2 — what the process delivers (dark 4-card band)
const UX_DELIVERABLES = [
  {
    icon: 'fa-diagram-project',
    title: 'Turn concepts into validated prototypes',
    desc: 'Transform your ideas into interactive prototypes before investing in full-scale development. Our UX design process uses wireframes, mockups, and prototypes to validate user flows, gather early feedback, and align product design with your business goals.',
  },
  {
    icon: 'fa-layer-group',
    title: 'Create consistent experiences across platforms',
    desc: 'We design responsive and adaptive interfaces for websites, mobile applications, SaaS products, and other digital platforms. Our UI/UX approach maintains visual consistency, intuitive navigation, and a seamless experience across different devices and screen sizes.',
  },
  {
    icon: 'fa-shuffle',
    title: 'Simplify user journeys and interactions',
    desc: 'We study how users interact with your digital product to identify friction points and improve navigation, workflows, and task completion. The result is a smoother, more intuitive user experience that encourages engagement and makes every interaction more effective.',
  },
  {
    icon: 'fa-universal-access',
    title: 'Design for accessibility and inclusion',
    desc: 'Accessibility is an integral part of our UI/UX design process. We follow recognized accessibility principles, including WCAG guidelines, to create digital experiences that are easier to use for people with different abilities — covering color contrast, keyboard navigation, readable interfaces, and screen-reader compatibility.',
  },
  
]

// Section 3 — design principles (icon grid, styled per the reference visual)
const UX_DESIGN_PRINCIPLES = [
  { icon: 'fa-palette',               title: 'Purposeful Color & Visual Design' },
  { icon: 'fa-font',                  title: 'Clear & Effective Typography' },
  { icon: 'fa-pen-ruler',             title: 'Structured Wireframes & Prototypes' },
  { icon: 'fa-mobile-screen-button',  title: 'Responsive & Adaptive Design' },
  { icon: 'fa-compass',               title: 'Intuitive Navigation & Information Architecture' },
  { icon: 'fa-route',                 title: 'User Journey & Interaction Mapping' },
  { icon: 'fa-universal-access',      title: 'Accessible & Inclusive Experiences' },
  { icon: 'fa-shapes',                title: 'Reusable Design Systems' },
  { icon: 'fa-hand-pointer',          title: 'Human-Centered & Intuitive Design' },
]

export default function UIUXDesign() {
  useEffect(() => { AOS.refresh(); window.scrollTo(0, 0) }, [])
useEffect(() => {
  AOS.refresh()
  window.scrollTo(0, 0)

  function initTree(dotId, branchIds, startX) {
    const dot = document.getElementById(dotId)
    if (!dot) return

    let currentBranch = 0
    let startTime = null

    const duration = 1200
    const pauseTime = 400

    function animateDot(timestamp) {
      if (!startTime) startTime = timestamp

      const elapsed = timestamp - startTime
      const path = document.getElementById(branchIds[currentBranch])

      if (!path) return

      const totalLen = path.getTotalLength()

      if (elapsed < duration) {
        let progress = elapsed / duration

        // ease-in / ease-out
        progress =
          progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress

        const point = path.getPointAtLength(progress * totalLen)

        dot.setAttribute('cx', point.x)
        dot.setAttribute('cy', point.y)

        requestAnimationFrame(animateDot)
      } else {
        const endPoint = path.getPointAtLength(totalLen)

        dot.setAttribute('cx', endPoint.x)
        dot.setAttribute('cy', endPoint.y)

        setTimeout(() => {
          // Reset to root
          dot.setAttribute('cx', startX)
          dot.setAttribute('cy', 0)

          // Next branch
          currentBranch =
            (currentBranch + 1) % branchIds.length

          startTime = null

          requestAnimationFrame(animateDot)
        }, pauseTime)
      }
    }

    setTimeout(() => {
      requestAnimationFrame(animateDot)
    }, 500)
  }

  initTree(
    'travel-dot-ux',
    [
      'ux-branch-1',
      'ux-branch-2',
      'ux-branch-3',
      'ux-branch-4',
    ],
    600
  )
}, [])

  return (
    <>
      {/* <MetaInfo
        title="UI/UX Design Services India | CruxBytes"
        description="CruxBytes delivers user-centered UI/UX design services — user research, wireframing, prototyping, and accessible interfaces for web, mobile, and SaaS products."
        keywords="UI UX design company India, UX design services, UI design agency, responsive design, accessibility design, wireframing prototyping"
      /> */}
       <MetaInfo
      title={`${label} ${SITE.metaTagPostText}`} 
      description={metaDesc}
      keywords=""
      />
      <main id="main-content" className="page-wrapper">
        <div className="page-hero">
          <div className="bg-grid bg-grid--fill" aria-hidden="true" />
          <div className="container">
            <Breadcrumb />
            <h1 data-aos="fade-up" data-aos-delay={100}>
              {/* UI/UX Design */}
              {label}
              </h1>
            <p className="page-subtitle" data-aos="fade-up" data-aos-delay={200}>
              {/* Craft engaging, accessible interfaces that simplify experiences and improve conversions. */}
              {pageDesc}
            </p>

            <div className="inner-page-actions">
              <Button href={ROUTES.SALES_ENQUIRY} variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-up" data-aos-delay={300}>
                Get a Free Quote
              </Button>
            </div>
          </div>
        </div>

        {/* ── Section 1: Process + intro ── */}
        <section className="sc-section section-py bg-linear-gradient-light" aria-labelledby="uiux-process-heading">
          <div className="container">
            <div className="text-center" data-aos="fade-up">
              As an experienced UI/UX design company in India, CruxBytes focuses on creating digital experiences that are visually refined, intuitive, functional, and easy to navigate.
            </div>
            {/* UX / UI Design Process Tree */}
<div className="policy-tree-section">
  <div className="text-center">
    <span className="policy-root" id="policy-root-node" data-aos="fade-up">
      UX / UI Design Process
    </span>
  </div>

  <div className="policy-tree-connector" data-aos="fade-up" data-aos-delay={100}>
  <svg
    id="policy-tree-svg"
    viewBox="0 0 1200 140"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
  >
    <path
      id="ux-branch-1"
      className="branch-line"
      d="M 600,0 L 600,45 Q 600,70 550,70 L 150,70 Q 130,70 130,95 L 130,140"
    />

    <path
      id="ux-branch-2"
      className="branch-line"
      d="M 600,0 L 600,45 Q 600,70 570,70 L 400,70 Q 380,70 380,95 L 380,140"
    />

    <path
      id="ux-branch-3"
      className="branch-line"
      d="M 600,0 L 600,45 Q 600,70 630,70 L 800,70 Q 820,70 820,95 L 820,140"
    />

    <path
      id="ux-branch-4"
      className="branch-line"
      d="M 600,0 L 600,45 Q 600,70 650,70 L 1050,70 Q 1070,70 1070,95 L 1070,140"
    />

    <circle
      className="travel-dot"
      id="travel-dot-ux"
      cx="600"
      cy="0"
      r="5"
    />
  </svg>
</div>

  <div className="policy-cards-row policy-cards-row--four">
   {UX_PROCESS_STEPS.map((step, i) => (
  <div className="policy-card" key={step.title} data-aos="fade-up" data-aos-delay={i * 100}>
    
    <span className="sc-bullet-icon" aria-hidden="true">
      <i className={`fa-solid ${step.icon}`} />
    </span>
    <span>{step.title}</span>
  </div>
))}
  </div>
</div>
            <div className="sc-row" data-aos="fade-up">
              <div className="sc-col-text">
                <p >
                  Our UI/UX design team combines user research, information architecture, wireframing, prototyping, and design systems to create engaging digital products for web, mobile, and SaaS platforms. We deliver practical, scalable design solutions that balance user needs with business objectives. Every interface is thoughtfully designed to help users accomplish their goals while supporting measurable business outcomes.
                </p>
              </div>

              <div className="sc-col-text">
                <p >
                  We place strong emphasis on usability and accessibility throughout the design process. By applying proven design principles and modern UX practices, we create interfaces that are consistent, inclusive, and effortless to use. Our approach goes beyond aesthetics to deliver meaningful digital experiences that encourage engagement and build user confidence.
                </p>

              
              </div>
            </div>
             <div className="text-center" data-aos="fade-up" >
              Whether you are a startup developing your first digital product or an established organization looking to improve an existing platform, our UI/UX designers can help you to discover hidden UX flaws and bring your product vision to life.
            </div>
                <div className="wcs-actions">
            <Button href={ROUTES.SALES_ENQUIRY} variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-left" >
              Talk To Our Experts
            </Button>                      
      </div>
          </div>
        </section>

        {/* ── Section 2: What our process delivers (dark 4-card band) ── */}
        <section className="sc-code-section section-py" aria-labelledby="uiux-delivers-heading">
          <svg className="sc-code-curve sc-code-curve--left" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="0" cy="0" r="100" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
            <circle cx="0" cy="0" r="180" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
            <circle cx="0" cy="0" r="260" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
          </svg>
          <svg className="sc-code-curve sc-code-curve--right" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="400" cy="0" r="100" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
            <circle cx="400" cy="0" r="180" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
            <circle cx="400" cy="0" r="260" stroke="rgba(255,255,255,0.5)" strokeWidth="1" fill="none" />
          </svg>

          <div className="container">
            <div className="sc-code-header" data-aos="fade-up">
              <h2 id="uiux-delivers-heading" className="sc-code-heading">
                What Our UI/UX Design Process Delivers
              </h2>
            </div>

            <div className="wcs-grid">
              {UX_DELIVERABLES.map((item, i) => (
                <div
                  key={item.title}
                  className="wcs-card bg-white"
                  data-aos="fade-up"
                  data-aos-delay={i * 80}
                >
                  {i > 0 && <div className="sc-code-divider" aria-hidden="true" />}
                  <i className={`fa-solid ${item.icon} wcs-icon`} aria-hidden="true" />
                  <h3 className="wcs-title">{item.title}</h3>
                  <p className="wcs-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 3: Design principles (icon grid) ── */}
        <section className="section-py" aria-labelledby="uiux-principles-heading">
          <div className="container">
            <div className="text-align-center" data-aos="fade-up">              
              <h2 id="uiux-principles-heading">UI/UX Design Principles We Bring to Every Product</h2>
            </div>

            <div className="icon-grid">
              {UX_DESIGN_PRINCIPLES.map((item, i) => (
                <div
                  key={item.title}
                  className="icon-grid-card"
                  data-aos="fade-up"
                  data-aos-delay={i * 50}
                >
                  <span className="sc-bullet-icon" aria-hidden="true">
                    <i className={`fa-solid ${item.icon}`} />
                  </span>
                  <span className="sc-bullet-title">{item.title}</span>
                  
                </div>
              ))}
            </div>
          </div>
        </section>

        <TransformBanner />
      </main>
    </>
  )
}